import { useEffect, useState } from "react";
import { api, ApiError, type WaitlistMeResponse } from "../api";
import { navigate, routes } from "../router";

interface Props {
  pending?: boolean;
}

type EmailFormState =
  | { kind: "loading" }
  | { kind: "idle"; needed: boolean; gh_login: string }
  | { kind: "submitting"; needed: boolean; gh_login: string; value: string }
  | { kind: "saved"; email: string }
  | { kind: "error"; message: string; needed: boolean; gh_login: string }
  // The pending cookie wasn't valid (expired, signed out, etc.).
  // We just hide the form section silently.
  | { kind: "no_pending_cookie" };

/**
 * Landing page after a non-allowlisted user completes GitHub OAuth.
 *
 * Two copies depending on whether this is the user's first attempt
 * (`pending=false`) or a return visit (`pending=true`). When GitHub
 * didn't give us a usable email (private email + "Keep private" on,
 * or scope denied), the page also renders a small inline form so the
 * user can supply one. See `collect-missing-email-on-waitlist`.
 */
export function RequestSubmitted({ pending = false }: Props) {
  const [state, setState] = useState<EmailFormState>({ kind: "loading" });
  const [draft, setDraft] = useState("");

  useEffect(() => {
    let cancelled = false;
    api
      .waitlistMe()
      .then((me: WaitlistMeResponse) => {
        if (cancelled) return;
        setState({
          kind: "idle",
          needed: !me.has_usable_email,
          gh_login: me.gh_login,
        });
      })
      .catch((e) => {
        if (cancelled) return;
        // 401 / 404 → no valid pending cookie: render the page without
        // the form, same as today.
        if (e instanceof ApiError && (e.status === 401 || e.status === 404)) {
          setState({ kind: "no_pending_cookie" });
          return;
        }
        setState({
          kind: "error",
          message: e instanceof Error ? e.message : String(e),
          needed: false,
          gh_login: "",
        });
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (state.kind !== "idle" && state.kind !== "error") return;
    const trimmed = draft.trim();
    if (!trimmed) return;
    setState({
      kind: "submitting",
      needed: state.needed,
      gh_login: state.gh_login,
      value: trimmed,
    });
    try {
      await api.setWaitlistEmail(trimmed);
      setState({ kind: "saved", email: trimmed });
      setDraft("");
    } catch (err) {
      setState({
        kind: "error",
        message: err instanceof Error ? err.message : String(err),
        needed: state.needed,
        gh_login: state.gh_login,
      });
    }
  };

  return (
    <div className="card">
      <h2 style={{ marginTop: 0 }}>
        {pending ? "Your request is still pending review" : "Thanks for requesting access"}
      </h2>
      <p>
        {pending
          ? "We'll let you know as soon as the admin reviews your request."
          : "We've notified the admin and they'll get back to you shortly. You can close this tab — we'll email you when you're approved."}
      </p>

      <EmailFormSection
        state={state}
        draft={draft}
        onDraftChange={setDraft}
        onSubmit={onSubmit}
      />

      <p className="muted" style={{ marginTop: 24 }}>
        If you signed in with the wrong GitHub account, sign out and try again.
      </p>
      <p style={{ marginTop: 16, display: "flex", gap: 12 }}>
        <button
          className="secondary"
          onClick={async () => {
            try {
              await api.logout();
            } catch {
              // best-effort
            }
            navigate(routes.home(), /*replace*/ true);
          }}
        >
          Sign out
        </button>
      </p>
    </div>
  );
}

function EmailFormSection({
  state,
  draft,
  onDraftChange,
  onSubmit,
}: {
  state: EmailFormState;
  draft: string;
  onDraftChange: (v: string) => void;
  onSubmit: (e: React.FormEvent) => void;
}) {
  if (state.kind === "loading" || state.kind === "no_pending_cookie") {
    return null;
  }
  if (state.kind === "saved") {
    return (
      <p className="muted" style={{ marginTop: 16 }}>
        Thanks — we'll reach you at <code>{state.email}</code>.
      </p>
    );
  }
  // For idle / submitting / error: only render the form if the row
  // actually needs an email. (idle.needed=false → user already has a
  // usable email, no form.)
  if (!state.needed) {
    return null;
  }
  const submitting = state.kind === "submitting";
  const errorMsg = state.kind === "error" ? state.message : null;
  return (
    <form
      onSubmit={onSubmit}
      style={{ marginTop: 16, display: "flex", flexDirection: "column", gap: 8 }}
    >
      <p className="muted" style={{ margin: 0 }}>
        GitHub kept your email private, so we don't have one to reach you on.
        Drop one below and we'll use it for the welcome email.
      </p>
      <div style={{ display: "flex", gap: 8 }}>
        <input
          type="email"
          placeholder="you@yourdomain.com"
          value={draft}
          onChange={(e) => onDraftChange(e.target.value)}
          disabled={submitting}
          required
          style={{ flex: 1 }}
        />
        <button type="submit" disabled={submitting || !draft.trim()}>
          {submitting ? "Saving…" : "Save"}
        </button>
      </div>
      {errorMsg ? (
        <p style={{ color: "var(--danger, #d33)", margin: 0 }}>{errorMsg}</p>
      ) : null}
    </form>
  );
}
